import { LogInIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAppStore } from "@/stores";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  capitalizeFirstLetter,
  getFullName,
  getUserInitials,
  sortByStringKey,
} from "@revelate/utils";
import useSWR from "swr";
import { TableType, getAll } from "@/lib/supabase";
import { Loader } from "@/components/Loader";
import { ConfirmDeletionDialog } from "@/components/ConfirmDeletionDialog";
import { EmployeeCombobox } from "./components/employee-combobox";
import { CurrencySelect } from "./components/currency-select";
import { useAPI } from "@/hooks/useAPI";
import { UserWithDeals } from "@revelate/types";
import { ErrorWithRetry } from "@/components/ErrorWithRetry";
import { ActiveLicenseToggle } from "./components/active-license-toggle";

export function EmployeesPage() {
  const appState = useAppStore((state) => state);
  const { scope, currentUser } = appState;

  const {
    data: users,
    error,
    isLoading,
    mutate,
  } = useSWR(
    [
      "users" as TableType,
      currentUser ? currentUser?.company_id?.toString() : null,
    ],
    ([table, companyId]): Promise<UserWithDeals[]> => getAll(table, companyId),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const { getMagicLink } = useAPI();

  if (isLoading) return <Loader />;
  if (error)
    return (
      <ErrorWithRetry
        error={error.message}
        onRetry={() => window.location.reload()}
      />
    );

  return (
    <main className="flex-1 sm:py-0">
      <Card x-chunk="dashboard-05-chunk-3" className="border-transparent">
        <CardHeader className="px-0 pt-3 pb-8">
          <CardTitle>
            <h1>
              Users
            </h1>
          </CardTitle>
        </CardHeader>
        <CardContent className="px-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead></TableHead>
                <TableHead className="sm:table-cell">Role</TableHead>
                <TableHead className="sm:table-cell">Currency</TableHead>
                {(currentUser?.role === "super_admin" ||
                  currentUser?.role === "admin") && (
                    <TableHead className="sm:table-cell">
                      Active License
                    </TableHead>
                  )}

                {/* Unlabeled action columns */}
                <TableHead />
                <TableHead />
              </TableRow>
            </TableHeader>

            <TableBody>
              {users
                ?.sort(sortByStringKey("first_name"))
                ?.map((user, index) => {
                  return (
                    <TableRow
                      className={user.id === scope?.value ? "bg-accent" : ""}
                      key={index}
                    >
                      {/* Avatar */}
                      <TableCell className="w-9">
                        <Avatar className="h-9 w-9">
                          <AvatarImage
                            src={user.avatar_url || ""}
                            alt="Avatar"
                          />
                          <AvatarFallback>
                            {getUserInitials(user)}
                          </AvatarFallback>
                        </Avatar>
                      </TableCell>

                      {/* Name */}
                      <TableCell className="pl-0">
                        <div className="font-medium">{getFullName(user)}</div>
                        <div className="hidden text-sm text-muted-foreground md:inline">
                          {user.email}
                        </div>
                      </TableCell>

                      {/* Role */}
                      <TableCell className="sm:table-cell">
                        {capitalizeFirstLetter(user?.role)}
                      </TableCell>

                      {/* Currency */}
                      <TableCell className="sm:table-cell">
                        <CurrencySelect user={user} mutate={mutate} />
                      </TableCell>

                      {/* Active license */}
                      <TableCell>
                        {(currentUser?.role === "super_admin" ||
                          currentUser?.role === "admin") && (
                            <div className="w-6">
                              <ActiveLicenseToggle user={user} mutate={mutate} />
                            </div>
                          )}
                      </TableCell>

                      {/* Transfer data */}
                      <TableCell className="px-0">
                        {(currentUser?.role === "super_admin" ||
                          currentUser?.role === "admin") && (
                            <EmployeeCombobox
                              user={user}
                              employees={users}
                              mutate={mutate}
                            />
                          )}
                      </TableCell>

                      {/* Login as */}
                      {currentUser?.role === "super_admin" && currentUser.id !== user?.id && (
                        <TableCell className="px-0">
                          <ConfirmDeletionDialog
                            key={`sign-in-as-${user.id}`}
                            title={`Are you sure you want to sign in as ${user.first_name}?`}
                            description="You will be signed out of your own account and need to sign in again."
                            deleteId={""}
                            deleteOperation={async () => {
                              const response = await getMagicLink(user.email);
                              const { magicLink } = response || {};
                              if (typeof magicLink === "string" && magicLink) window.open(magicLink, "_self");
                            }}
                          >
                            <Button
                              size="icon"
                              variant="default"
                            >
                              <LogInIcon className="h-4 w-4" />
                            </Button>
                          </ConfirmDeletionDialog>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </main>
  );
}
